import { useFetchData } from "../../../hooks/useFetch";
import { BaseResponse } from "../../../models/response/base-response";
import { ApiConfig, API_WAGE_RATES_ROUTER } from "../../../services/api-config";

export enum EMPLOYEE_ROLE_TYPE {
  PICKUP = 1,
  LAUNDRY = 2,
  DELIVERY = 3,
  INSPECTOR = 4,
  WASHER = 5,
  DRYER = 6,
  IRONER = 7,
  FOLDER = 8,
}

export const ROLE_TYPE_LABELS: Record<number, string> = {
  [EMPLOYEE_ROLE_TYPE.PICKUP]: "Lấy hàng",
  [EMPLOYEE_ROLE_TYPE.LAUNDRY]: "Giặt",
  [EMPLOYEE_ROLE_TYPE.DELIVERY]: "Giao hàng",
  [EMPLOYEE_ROLE_TYPE.WASHER]: "Giặt",
  [EMPLOYEE_ROLE_TYPE.DRYER]: "Sấy",
  [EMPLOYEE_ROLE_TYPE.IRONER]: "Ủi",
  [EMPLOYEE_ROLE_TYPE.FOLDER]: "Gấp",
};

export interface WageRate {
  id: number;
  employee_id?: number;
  branch_id?: number;
  role_type: number;
  rate_per_kg: number;
  effective_from: string;
  effective_to?: string;
  status: number;
  note?: string;
  created_at: string;
  updated_at: string;
  employee?: {
    id: number;
    name: string;
  };
  branch?: {
    id: number;
    name: string;
  };
}

export interface WageRateListFilters {
  employee_id?: number;
  branch_id?: number;
  role_type?: number;
  status?: number;
  page?: number;
  limit?: number;
}

export interface WageRateListResponse {
  status: number;
  message: string;
  data: WageRate[];
}

export interface CreateWageRatePayload {
  employee_id?: number;
  branch_id?: number;
  role_type: number;
  rate_per_kg: number;
  effective_from: string;
  effective_to?: string;
  note?: string;
}

export interface UpdateWageRatePayload {
  rate_per_kg?: number;
  effective_to?: string;
  status?: number;
  note?: string;
}

export const WageRatesService = {
  list: () => {
    const { request, baseResponse, loading, error } = useFetchData.Get<WageRateListResponse>({
      projectId: ApiConfig.PROJECT_ID.USER_SERVICE,
    });
    return {
      fetch: (params: WageRateListFilters = {}) =>
        request({
          endPoint: API_WAGE_RATES_ROUTER.API_LIST,
          data: params,
        }),
      response: baseResponse,
      loading,
      error,
    };
  },

  active: () => {
    const { request, baseResponse, loading, error } = useFetchData.Get<WageRateListResponse>({
      projectId: ApiConfig.PROJECT_ID.USER_SERVICE,
    });
    return {
      fetch: () =>
        request({
          endPoint: API_WAGE_RATES_ROUTER.API_ACTIVE,
        }),
      response: baseResponse,
      loading,
      error,
    };
  },

  create: () => {
    const { request, baseResponse, loading } = useFetchData.Post<BaseResponse<WageRate>>({
      projectId: ApiConfig.PROJECT_ID.USER_SERVICE,
    });
    return {
      fetch: (payload: CreateWageRatePayload) =>
        request({
          endPoint: API_WAGE_RATES_ROUTER.API_CREATE,
          data: payload,
        }),
      response: baseResponse,
      loading,
    };
  },

  update: () => {
    const { request, baseResponse, loading } = useFetchData.Post<BaseResponse<WageRate>>({
      projectId: ApiConfig.PROJECT_ID.USER_SERVICE,
    });
    return {
      fetch: (id: number, payload: UpdateWageRatePayload) =>
        request({
          endPoint: API_WAGE_RATES_ROUTER.API_UPDATE(id),
          data: payload,
        }),
      response: baseResponse,
      loading,
    };
  },

  delete: () => {
    const { request, baseResponse, loading } = useFetchData.Post<BaseResponse<null>>({
      projectId: ApiConfig.PROJECT_ID.USER_SERVICE,
    });
    return {
      fetch: (id: number) =>
        request({
          endPoint: API_WAGE_RATES_ROUTER.API_DELETE(id),
          data: {},
        }),
      response: baseResponse,
      loading,
    };
  },

  /**
   * Lấy đơn giá áp dụng cho nhân viên theo role_type, employee_id, branch_id
   */
  getApplicable: () => {
    const { request, baseResponse, loading } = useFetchData.Get<BaseResponse<WageRate>>({
      projectId: ApiConfig.PROJECT_ID.USER_SERVICE,
    });
    return {
      fetch: (params: { role_type: number; employee_id: number; branch_id?: number }) =>
        request({
          endPoint: API_WAGE_RATES_ROUTER.API_APPLICABLE,
          data: params,
        }),
      response: baseResponse,
      loading,
    };
  },
};

export default WageRatesService;
